/**
 * The backdrop atlas (lifted from the shared prototype engine's
 * `engine/src/backdrop-art.js`): one 512 × 560 pixel atlas, painted at run
 * time on a canvas, carries every backdrop wall bay, gable, hedge, woodpile,
 * laundry and vegetable surface, with a matching emissive mask for lit and
 * night-lit panes. One material (`backdropMaterial`, shared per kit) keeps the
 * whole unreachable quarter in one draw; per-vertex tint and baked occlusion
 * give variety. `AtlasKit` accumulates quads with per-vertex tint into one
 * non-indexed geometry whose attributes (position, normal, uv, color) match
 * the static batcher's. The painting arithmetic stays as lifted; no text.
 */
import * as THREE from 'three';
import { seededRandom } from '../grid.ts';
import type { Kit } from './kit.ts';

export const CELL_W = 64;
export const CELL_H = 80;
export const ATLAS = 512;
export const ATLAS_H = 560;

/** Cell indices (column + row × 8). */
export const CELLS = {
  plaster: 0,
  winGreen: 1,
  winRed: 2,
  winPlain: 3,
  winLit: 4,
  winFlowers: 5,
  winClosed: 6,
  winIvy: 7,
  timberX: 8,
  timberWin: 9,
  timberLit: 10,
  timberShut: 11,
  timberStud: 12,
  timberBrace: 13,
  timberIvy: 14,
  timberSmall: 15,
  doorTeal: 16,
  doorBrown: 17,
  archWin: 18,
  stone: 19,
  shopLit: 20,
  barred: 21,
  passage: 22,
  stoneIvy: 23,
  brick: 24,
  brickWin: 25,
  brickLit: 26,
  brickShut: 27,
  gableRound: 28,
  gableLouvre: 29,
  winNight: 30,
  timberNight: 31,
  hedge: 32,
  logs: 33,
  laundry: 34,
  vegRows: 35,
  planks: 36,
  drystone: 37,
  soil: 38,
  straw: 39,
  shedDoor: 40,
  bark: 41,
  shade: 42,
  cabbage: 43,
  flowerBed: 44,
  ivy: 45,
  roofTile: 46,
  darkWood: 47,
  winCurtain: 48,
  winHalf: 49,
  winPair: 50,
  winOffset: 51,
  timberCurtain: 52,
  brickCurtain: 53,
  winNight2: 54,
  timberNight2: 55,
} as const;

type WindowOptions = {
  readonly x?: number;
  readonly y?: number;
  readonly w?: number;
  readonly h?: number;
  readonly lit?: boolean;
  readonly nightLit?: boolean;
  readonly shutters?: readonly [string, string] | null;
  readonly closed?: boolean;
  readonly halfShut?: boolean;
  readonly flowers?: boolean;
  readonly curtains?: boolean;
  readonly reflect?: boolean;
  readonly frame?: string;
};

const paintAtlas = (): { readonly map: THREE.CanvasTexture; readonly emissiveMap: THREE.CanvasTexture } => {
  const c = document.createElement('canvas');
  c.width = ATLAS;
  c.height = ATLAS_H;
  const e = document.createElement('canvas');
  e.width = ATLAS;
  e.height = ATLAS_H;
  const g = c.getContext('2d');
  const eg = e.getContext('2d');
  if (g === null || eg === null) throw new Error('no 2D canvas context for the backdrop atlas');
  eg.fillStyle = '#000';
  eg.fillRect(0, 0, ATLAS, ATLAS_H);
  const r = seededRandom(40417);
  const px = (x: number, y: number, w: number, h: number, color: string): void => {
    g.fillStyle = color;
    g.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  };
  const pick = (list: readonly string[]): string => list[Math.floor(r() * list.length)] ?? list[0] ?? '#000';
  const speckle = (ox: number, oy: number, w: number, h: number, tones: readonly string[], count: number): void => {
    for (let i = 0; i < count; i += 1) px(ox + Math.floor(r() * w), oy + Math.floor(r() * h), 1 + Math.floor(r() * 3), 1 + Math.floor(r() * 1.4), pick(tones));
  };
  const plasterBase = (ox: number, oy: number): void => {
    px(ox, oy, CELL_W, CELL_H, '#b9a78d');
    for (let i = 0; i < 8; i += 1) {
      const x = ox + r() * CELL_W;
      const y = oy + r() * CELL_H;
      for (let k = 0; k < 26; k += 1) px(x + (r() - 0.5) * 18, y + (r() - 0.5) * 14, 2 + r() * 3, 2, i % 2 ? '#c4b295' : '#a8957a');
    }
    speckle(ox, oy, CELL_W, CELL_H, ['#9d8a70', '#a38f73', '#c7b69b', '#8f7c63'], 150);
    for (let y = 0; y < 12; y += 1) for (let x = 0; x < CELL_W; x += 1) if (r() < 0.55 - y * 0.045) px(ox + x, oy + CELL_H - 5 - y, 1, 1, '#8e7b62');
    const eave = ['#5f4f3e', '#76644f', '#8e7b63', '#a18d72'];
    for (let y = 0; y < 4; y += 1) px(ox, oy + y, CELL_W, 1, eave[y] ?? '#5f4f3e');
    px(ox, oy + CELL_H - 5, CELL_W, 4, '#cbbb9c');
    px(ox, oy + CELL_H - 5, CELL_W, 1, '#d9cab0');
    px(ox, oy + CELL_H - 1, CELL_W, 1, '#5d5043');
  };
  const brickBase = (ox: number, oy: number): void => {
    px(ox, oy, CELL_W, CELL_H, '#7b5a4b');
    for (let y = 0; y < CELL_H - 5; y += 5) {
      const shift = (y / 5) % 2 ? 5 : 0;
      for (let x = -shift; x < CELL_W; x += 10) {
        px(ox + Math.max(0, x), oy + y, Math.min(9, 9 + x) - Math.max(0, -x), 4, pick(['#b27a5e', '#a86f55', '#bd876a', '#9c6a55', '#b98068']));
        if (r() < 0.4) px(ox + Math.max(0, x), oy + y, 4, 1, '#cf9c7f');
      }
    }
    px(ox, oy + CELL_H - 5, CELL_W, 4, '#e9dcc4');
    px(ox, oy + CELL_H - 1, CELL_W, 1, '#6f6356');
  };
  const stoneBase = (ox: number, oy: number): void => {
    px(ox, oy, CELL_W, CELL_H, '#77736b');
    for (let y = 0; y < CELL_H; y += 9) {
      let x = -Math.floor(r() * 12);
      while (x < CELL_W) {
        const w = 12 + Math.floor(r() * 12);
        const tone = pick(['#b5ada0', '#a79f93', '#bdb6a9', '#9c968c', '#aca496']);
        px(ox + Math.max(0, x) + 1, oy + y + 1, Math.min(w - 2, CELL_W - x - 1), 7, tone);
        px(ox + Math.max(0, x) + 1, oy + y + 1, Math.min(w - 3, CELL_W - x - 1), 1, '#cdc6b9');
        px(ox + Math.max(0, x) + 1, oy + y + 7, Math.min(w - 2, CELL_W - x - 1), 1, '#8a857c');
        x += w;
      }
    }
    speckle(ox, oy, CELL_W, CELL_H, ['#8f8a80', '#c3bcae'], 60);
    for (let y = 0; y < 14; y += 1) for (let x = 0; x < CELL_W; x += 1) if (r() < 0.45 - y * 0.03) px(ox + x, oy + CELL_H - 1 - y, 1, 1, '#6f7a63');
  };
  const timberFrame = (ox: number, oy: number, brace: 'x' | 'stud' | 'brace' | 'window'): void => {
    plasterBase(ox, oy);
    const beam = '#4f3d2f';
    const hi = '#6d5641';
    px(ox, oy, 5, CELL_H - 5, beam);
    px(ox + CELL_W - 5, oy, 5, CELL_H - 5, beam);
    px(ox + 1, oy, 1, CELL_H - 5, hi);
    px(ox, oy, CELL_W, 4, beam);
    px(ox, oy + CELL_H - 10, CELL_W, 5, beam);
    px(ox, oy + CELL_H - 10, CELL_W, 1, hi);
    const diag = (x0: number, y0: number, x1: number, y1: number): void => {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
      for (let i = 0; i <= n; i += 1) {
        const t = i / n;
        px(ox + x0 + (x1 - x0) * t - 1, oy + y0 + (y1 - y0) * t, 4, 2, beam);
      }
    };
    if (brace === 'x') {
      diag(4, 4, CELL_W - 6, CELL_H - 11);
      diag(CELL_W - 6, 4, 4, CELL_H - 11);
    } else if (brace === 'stud') {
      px(ox + 29, oy, 5, CELL_H - 5, beam);
      px(ox, oy + 34, CELL_W, 4, beam);
    } else if (brace === 'brace') {
      diag(4, CELL_H - 11, 26, 4);
      diag(CELL_W - 6, CELL_H - 11, 38, 4);
    } else {
      px(ox + 12, oy, 3, CELL_H - 5, beam);
      px(ox + CELL_W - 15, oy, 3, CELL_H - 5, beam);
      diag(4, CELL_H - 11, 12, CELL_H - 30);
      diag(CELL_W - 6, CELL_H - 11, CELL_W - 14, CELL_H - 30);
    }
  };
  const windowAt = (ox: number, oy: number, o: WindowOptions = {}): void => {
    const { x = 20, y = 14, w = 24, h = 38, lit = false, nightLit = false, shutters = null, closed = false, halfShut = false, flowers = false, curtains = false, reflect = false, frame = '#d8cbb0' } = o;
    const X = ox + x;
    const Y = oy + y;
    px(X - 4, Y - 7, w + 8, 4, '#cdbd9f');
    px(X - 4, Y - 7, w + 8, 1, '#e0d2b6');
    if (shutters) {
      const [s, sd] = shutters;
      const sides = closed ? [X, X + w / 2] : halfShut ? [X - 10, X + w / 2] : [X - 10, X + w + 1];
      sides.forEach((sx, k) => {
        if (halfShut && k === 1) return;
        const sw = closed ? w / 2 : 9;
        px(sx, Y, sw, h, s);
        for (let yy = Y + 2; yy < Y + h - 1; yy += 3) px(sx + 1, yy, sw - 2, 1, sd);
        px(sx + sw - 1, Y, 1, h, '#00000055');
      });
      if (closed) {
        px(X + w / 2 - 1, Y, 1, h, '#221c18');
        px(X - 2, Y + h, w + 4, 3, '#c9b999');
        px(X - 2, Y + h + 3, w + 4, 3, '#4a3f35');
        return;
      }
    }
    px(X - 2, Y - 3, w + 4, h + 3, frame);
    px(X - 2, Y - 3, w + 4, 1, '#e9dcc0');
    const paneW = halfShut ? w / 2 : w;
    px(X, Y, paneW, h, lit ? '#a86a38' : '#27323a');
    if (lit) {
      for (let yy = 0; yy < h; yy += 1) for (let xx = 0; xx < paneW; xx += 1) if (r() < 0.22) px(X + xx, Y + yy, 1, 1, yy < h * 0.5 ? '#d49c58' : '#8a5530');
    } else {
      for (let k = 0; k < 6; k += 1) px(X + 1 + k * 2, Y + 1 + k, paneW * 0.35 - k, 1, reflect ? '#c99a62' : '#4f6573');
      if (reflect) px(X + paneW - 6, Y + 3, 3, 2, '#e8c48a');
      speckle(X, Y + h * 0.5, paneW, h * 0.5, ['#1f282f', '#2e3b44'], 18);
    }
    if (lit || nightLit) {
      eg.fillStyle = lit ? '#ffd58a' : '#e0a860';
      eg.fillRect(X + 2, Y + 2, paneW - 2, h - 2);
    }
    if (curtains || lit) {
      const cw = Math.max(3, Math.round(paneW * 0.22));
      const col = lit ? '#6e3a24' : '#6f4a3c';
      for (const cx of [X, X + paneW - cw]) {
        px(cx, Y, cw, h, col);
        for (let yy = Y; yy < Y + h; yy += 4) px(cx + (cx === X ? cw - 1 : 0), yy, 1, 2, '#00000040');
        eg.fillStyle = '#000';
        eg.fillRect(cx, Y, cw, h);
      }
    }
    px(X, Y, paneW, 3, lit ? '#6e4424' : '#141b20');
    px(X, Y, 3, h, lit ? '#6e4424' : '#141b20');
    eg.fillStyle = '#000';
    eg.fillRect(X, Y, paneW, 2);
    const mull = lit ? '#3e2e20' : frame;
    if (paneW === w) {
      px(X + w / 2 - 1, Y, 2, h, mull);
      eg.fillRect(X + w / 2 - 1, Y, 2, h);
    }
    for (const t of [0.33, 0.66]) {
      px(X, Y + Math.round(h * t), paneW, 1, mull);
      eg.fillRect(X, Y + Math.round(h * t), paneW, 1);
    }
    if (halfShut && shutters) {
      const [sc, sd] = shutters;
      px(X + w / 2, Y, w / 2, h, sc);
      for (let yy = Y + 2; yy < Y + h - 1; yy += 3) px(X + w / 2 + 1, yy, w / 2 - 2, 1, sd);
      px(X + w / 2, Y, 1, h, '#00000055');
    }
    px(X - 3, Y + h, w + 6, 3, '#cbbc9d');
    px(X - 3, Y + h + 3, w + 6, 1, '#3e342b');
    px(X - 2, Y + h + 4, w + 4, 1, '#5a4d40');
    px(X - 1, Y + h + 5, w + 2, 1, '#00000030');
    if (flowers) {
      px(X - 1, Y + h - 5, w + 2, 6, '#5a3d2b');
      for (let i = 0; i < 26; i += 1) px(X - 1 + r() * (w + 1), Y + h - 9 + r() * 5, 2, 2, pick(['#3f5a31', '#56723d', '#6a8448']));
      for (let i = 0; i < 14; i += 1) px(X + r() * w, Y + h - 10 + r() * 5, 2, 2, pick(['#c45a70', '#d9c8c0', '#b8383f', '#d6a444']));
      for (let i = 0; i < 6; i += 1) px(X + r() * w, Y + h + 1 + r() * 7, 1, 2 + r() * 3, '#46613a');
    }
  };
  const ivyOver = (ox: number, oy: number, x0: number, x1: number, y0: number): void => {
    for (let x = x0; x < x1; x += 1) {
      for (let y = y0 + Math.floor(Math.abs(Math.sin(x * 0.37)) * 16); y < CELL_H; y += 1) {
        if (r() < 0.62) px(ox + x, oy + y, 1 + (r() < 0.3 ? 1 : 0), 1, pick(['#3a5234', '#4f6b3e', '#62804a', '#2f432d']));
      }
    }
  };
  const cell = (index: number): readonly [number, number] => [(index % 8) * CELL_W, Math.floor(index / 8) * CELL_H];
  const green = ['#46695a', '#39574b'] as const;
  const red = ['#8a4a3a', '#723b2f'] as const;
  const blue = ['#4c6178', '#3d4f63'] as const;
  const plasterWin = (i: number, o: WindowOptions): void => {
    const [x, y] = cell(i);
    plasterBase(x, y);
    windowAt(x, y, o);
  };
  const timberWin = (i: number, o: WindowOptions): void => {
    const [x, y] = cell(i);
    timberFrame(x, y, 'window');
    windowAt(x, y, o);
  };
  const brickWin = (i: number, o: WindowOptions): void => {
    const [x, y] = cell(i);
    brickBase(x, y);
    windowAt(x, y, o);
  };
  // Row 0: limewashed plaster storeys.
  plasterBase(...cell(0));
  plasterWin(1, { shutters: green });
  plasterWin(2, { shutters: red });
  plasterWin(3, { x: 18, w: 28, reflect: true });
  plasterWin(4, { lit: true, shutters: blue });
  plasterWin(5, { flowers: true, shutters: green });
  plasterWin(6, { shutters: red, closed: true });
  plasterWin(7, { x: 22, w: 20, h: 32 });
  ivyOver(...cell(7), 0, 30, 30);
  // Row 1: half-timbered storeys.
  timberFrame(...cell(8), 'x');
  timberWin(9, { frame: '#d2c4a8' });
  timberWin(10, { lit: true, frame: '#d2c4a8' });
  timberWin(11, { x: 22, w: 20, shutters: green });
  timberFrame(...cell(12), 'stud');
  timberFrame(...cell(13), 'brace');
  timberFrame(...cell(14), 'stud');
  ivyOver(...cell(14), 20, 64, 24);
  timberWin(15, { x: 24, y: 22, w: 16, h: 24 });
  // Row 2: stone ground floors, doors and a dark through-passage.
  const door = (ox: number, oy: number, color: string, dark: string): void => {
    stoneBase(ox, oy);
    px(ox + 17, oy + 18, 30, 62, '#c3b79f');
    px(ox + 20, oy + 22, 24, 58, color);
    for (let x = ox + 23; x < ox + 44; x += 5) px(x, oy + 22, 1, 58, dark);
    px(ox + 20, oy + 22, 24, 2, '#00000040');
    px(ox + 38, oy + 52, 2, 3, '#c8a563');
    px(ox + 14, oy + 16, 36, 4, '#cabda2');
    px(ox + 14, oy + 76, 36, 4, '#9b958a');
  };
  door(...cell(16), '#3f6b64', '#30524d');
  door(...cell(17), '#6d4c35', '#553a28');
  {
    const [ox, oy] = cell(18);
    stoneBase(ox, oy);
    for (let y = 0; y < 12; y += 1) {
      const half = Math.round(Math.sqrt(Math.max(0, 144 - (12 - y) ** 2)));
      px(ox + 32 - half - 3, oy + 12 + y, half * 2 + 6, 1, '#d2c4a8');
      px(ox + 32 - half, oy + 14 + y, half * 2, 1, '#2d3b45');
    }
    windowAt(ox, oy, { x: 20, y: 26, w: 24, h: 30, frame: '#d2c4a8' });
  }
  stoneBase(...cell(19));
  {
    const [ox, oy] = cell(20);
    stoneBase(ox, oy);
    windowAt(ox, oy, { x: 8, y: 26, w: 48, h: 34, lit: true, frame: '#d7cdb6' });
    for (let x = 0; x < 64; x += 8) {
      px(ox + x, oy + 8, 4, 12, '#8f3f38');
      px(ox + x + 4, oy + 8, 4, 12, '#efe4cc');
    }
    px(ox, oy + 20, 64, 2, '#00000050');
  }
  {
    const [ox, oy] = cell(21);
    stoneBase(ox, oy);
    px(ox + 22, oy + 30, 20, 16, '#1f252b');
    for (let x = ox + 24; x < ox + 42; x += 4) px(x, oy + 30, 1, 16, '#4b4a45');
    px(ox + 20, oy + 28, 24, 2, '#d6cebd');
    px(ox + 20, oy + 46, 24, 2, '#d6cebd');
  }
  {
    // A deep vaulted passage: the street beyond is implied, never reachable.
    const [ox, oy] = cell(22);
    stoneBase(ox, oy);
    const half = (y: number, rad: number): number => (y < rad ? Math.round(Math.sqrt(Math.max(0, rad * rad - (rad - y) ** 2))) : rad);
    for (let y = 0; y < 68; y += 1) {
      const w = half(y, 22);
      px(ox + 32 - w, oy + 12 + y, w * 2, 1, y < 3 ? '#efe6d3' : '#d9d0bf');
    }
    for (let y = 0; y < 64; y += 1) {
      const top = half(y, 16);
      const shade = y / 64;
      px(ox + 32 - top, oy + 16 + y, top * 2, 1, `rgb(${Math.round(30 + shade * 22)},${Math.round(27 + shade * 18)},${Math.round(40 + shade * 10)})`);
    }
    px(ox + 27, oy + 58, 10, 22, '#6d6a5a');
    px(ox + 28, oy + 60, 8, 20, '#a39a78');
    speckle(ox + 28, oy + 70, 8, 10, ['#8b8d62', '#b7ac85'], 10);
  }
  stoneBase(...cell(23));
  ivyOver(...cell(23), 0, 64, 6);
  // Row 3: brick storeys and gable ends.
  brickBase(...cell(24));
  brickWin(25, { frame: '#d2c4a8' });
  brickWin(26, { lit: true, frame: '#d2c4a8' });
  brickWin(27, { shutters: green, frame: '#d2c4a8' });
  {
    const [ox, oy] = cell(28);
    plasterBase(ox, oy);
    for (let y = -7; y <= 7; y += 1) {
      const half = Math.round(Math.sqrt(49 - y * y));
      px(ox + 32 - half - 2, oy + 40 + y, half * 2 + 4, 1, '#d2c4a8');
    }
    for (let y = -5; y <= 5; y += 1) {
      const half = Math.round(Math.sqrt(25 - y * y));
      px(ox + 32 - half, oy + 40 + y, half * 2, 1, '#2d3b45');
    }
    px(ox + 31, oy + 35, 2, 11, '#d2c4a8');
  }
  {
    const [ox, oy] = cell(29);
    plasterBase(ox, oy);
    px(ox + 24, oy + 30, 16, 22, '#cbbd9f');
    for (let y = oy + 32; y < oy + 50; y += 3) px(ox + 26, y, 12, 2, '#4d3f33');
  }
  // Dark by day, lit only once the night ramp raises emissive intensity.
  plasterWin(30, { nightLit: true, shutters: green });
  timberWin(31, { nightLit: true, frame: '#d2c4a8' });
  // Row 4: garden and yard surfaces.
  {
    const [ox, oy] = cell(32);
    px(ox, oy, CELL_W, CELL_H, '#2b3d2c');
    const tones = ['#2f4630', '#3e5a3a', '#557449', '#6f8c56', '#8fa46a'];
    for (let i = 0; i < 900; i += 1) {
      const x = r() * CELL_W;
      const y = r() * CELL_H;
      const light = 1 - y / CELL_H + (r() - 0.5) * 0.6;
      px(ox + x, oy + y, 2 + r() * 2, 1 + r() * 2, tones[Math.max(0, Math.min(4, Math.floor(light * 4.2)))] ?? '#2f4630');
    }
  }
  {
    const [ox, oy] = cell(33);
    px(ox, oy, CELL_W, CELL_H, '#3a2c22');
    for (let y = 4; y < CELL_H; y += 9) {
      for (let x = (y / 9) % 2 ? 0 : 5; x < CELL_W; x += 10) {
        const rad = 4 + r() * 1.2;
        for (let yy = -4; yy <= 4; yy += 1) {
          for (let xx = -5; xx <= 5; xx += 1) {
            const d = Math.hypot(xx, yy);
            if (d > rad) continue;
            px(ox + x + xx, oy + y + yy, 1, 1, d > rad - 1.2 ? '#5d4632' : d < 1.5 ? '#9f7d55' : Math.round(d) % 2 ? '#c9a878' : '#b8966a');
          }
        }
      }
    }
  }
  {
    // Four hanging cloths, sampled individually by laundry quads.
    const [ox, oy] = cell(34);
    const cloths: readonly (readonly [string, string])[] = [
      ['#cfc3aa', '#b3a68c'],
      ['#8f9c98', '#77837f'],
      ['#b0705c', '#935a49'],
      ['#c2a468', '#a28750'],
    ];
    cloths.forEach(([a, b], i) => {
      const x = ox + i * 16;
      px(x, oy, 16, CELL_H, a);
      for (let k = 2; k < 16; k += 5) px(x + k, oy, 2, CELL_H, b);
      px(x, oy, 16, 2, b);
      if (i === 2) for (let y = 10; y < CELL_H; y += 12) px(x, oy + y, 16, 3, '#cdb9a4');
      if (i === 1) for (let k = 0; k < 16; k += 6) px(x + k, oy, 1, CELL_H, '#66736f');
      px(x, oy + CELL_H - 3, 16, 3, b);
    });
  }
  {
    const [ox, oy] = cell(35);
    px(ox, oy, CELL_W, CELL_H, '#6b5341');
    speckle(ox, oy, CELL_W, CELL_H, ['#5b4536', '#7c6350', '#846a54'], 200);
    for (let y = 6; y < CELL_H; y += 13) {
      for (let x = 2; x < CELL_W; x += 7) {
        const s = 2 + r() * 2;
        px(ox + x, oy + y, 5, 4 + s, '#3f5b35');
        px(ox + x + 1, oy + y - 1, 3, 3, '#6d8e4a');
        px(ox + x + 1, oy + y + 1, 2, 2, '#8fae62');
      }
    }
  }
  {
    const [ox, oy] = cell(36);
    for (let x = 0; x < CELL_W; x += 8) {
      px(ox + x, oy, 8, CELL_H, pick(['#8a6d4f', '#7a5f45', '#937657', '#6f563f']));
      px(ox + x, oy, 1, CELL_H, '#4a3a2b');
      px(ox + x + 2, oy + r() * 60, 1, 8 + r() * 10, '#a58a69');
    }
    speckle(ox, oy, CELL_W, CELL_H, ['#5f4b38', '#9d8263'], 60);
  }
  {
    const [ox, oy] = cell(37);
    px(ox, oy, CELL_W, CELL_H, '#4c4a44');
    for (let i = 0; i < 70; i += 1) {
      const w = 7 + r() * 9;
      const h = 4 + r() * 4;
      const x = ox + r() * (CELL_W - w);
      const y = oy + r() * (CELL_H - h);
      px(x, y, w, h, pick(['#a39c8c', '#8f897b', '#b3ab98', '#9a9483']));
      px(x, y, w - 1, 1, '#c6bfad');
      if (r() < 0.25) px(x + 1, y + h - 2, w - 2, 2, '#6f8157');
    }
  }
  {
    const [ox, oy] = cell(38);
    px(ox, oy, CELL_W, CELL_H, '#72594a');
    speckle(ox, oy, CELL_W, CELL_H, ['#5f4a3d', '#836a58', '#8e7662', '#584236'], 380);
  }
  {
    const [ox, oy] = cell(39);
    px(ox, oy, CELL_W, CELL_H, '#b89b58');
    for (let i = 0; i < 500; i += 1) px(ox + r() * CELL_W, oy + r() * CELL_H, 1, 2 + r() * 4, pick(['#d8bd72', '#a4884a', '#cdb06a', '#8e7640']));
  }
  // Row 5: sheds, bark, shadow, beds, ivy, roof tile, dark timber.
  {
    const [ox, oy] = cell(40);
    for (let x = 0; x < CELL_W; x += 8) {
      px(ox + x, oy, 8, CELL_H, pick(['#6f5a47', '#65513f', '#7a634d']));
      px(ox + x, oy, 1, CELL_H, '#3f3226');
    }
    px(ox + 14, oy + 22, 36, 58, '#4a5c52');
    for (let x = ox + 18; x < ox + 50; x += 6) px(x, oy + 22, 1, 58, '#394840');
    px(ox + 14, oy + 22, 36, 2, '#2c2c28');
    px(ox + 40, oy + 50, 2, 3, '#b39a64');
  }
  {
    const [ox, oy] = cell(41);
    px(ox, oy, CELL_W, CELL_H, '#5a4636');
    for (let x = 0; x < CELL_W; x += 3) px(ox + x, oy, 1, CELL_H, pick(['#4a392c', '#6b5441', '#3f3127']));
  }
  {
    const [ox, oy] = cell(42);
    for (let y = 0; y < CELL_H; y += 1) {
      const t = y / CELL_H;
      px(ox, oy + y, CELL_W, 1, `rgb(${Math.round(58 - t * 28)},${Math.round(54 - t * 26)},${Math.round(70 - t * 30)})`);
    }
    speckle(ox, oy + 40, CELL_W, 40, ['#262431', '#34303d'], 50);
  }
  {
    const [ox, oy] = cell(43);
    px(ox, oy, CELL_W, CELL_H, '#6b5341');
    speckle(ox, oy, CELL_W, CELL_H, ['#5b4536', '#7c6350'], 150);
    for (let y = 8; y < CELL_H; y += 16) {
      for (let x = 6; x < CELL_W; x += 13) {
        for (let k = 0; k < 18; k += 1) px(ox + x + (r() - 0.5) * 9, oy + y + (r() - 0.5) * 8, 2, 2, pick(['#6f955a', '#8db46e', '#5a7d4b', '#a6c784']));
        px(ox + x - 1, oy + y - 1, 3, 3, '#c3dca0');
      }
    }
  }
  {
    const [ox, oy] = cell(44);
    px(ox, oy, CELL_W, CELL_H, '#3f5a37');
    speckle(ox, oy, CELL_W, CELL_H, ['#4f6d41', '#5f7f4c', '#34502f'], 260);
    for (let i = 0; i < 90; i += 1) px(ox + r() * CELL_W, oy + r() * CELL_H, 2, 2, pick(['#e8d9e6', '#e27d95', '#f2cf62', '#b58ad6', '#f7f1e4']));
  }
  {
    const [ox, oy] = cell(45);
    px(ox, oy, CELL_W, CELL_H, '#2a3c29');
    for (let i = 0; i < 700; i += 1) px(ox + r() * CELL_W, oy + r() * CELL_H, 2, 2, pick(['#3a5234', '#4f6b3e', '#62804a', '#2f432d', '#76935a']));
  }
  {
    const [ox, oy] = cell(46);
    px(ox, oy, CELL_W, CELL_H, '#8c4c3a');
    for (let y = 0; y < CELL_H; y += 6) {
      for (let x = (y / 6) % 2 ? -4 : 0; x < CELL_W; x += 8) {
        px(ox + x + 1, oy + y, 6, 5, pick(['#b8664a', '#a95b43', '#c5765a', '#9b523f']));
        px(ox + x + 1, oy + y + 4, 6, 1, '#6f3a2c');
      }
    }
  }
  {
    const [ox, oy] = cell(47);
    px(ox, oy, CELL_W, CELL_H, '#4b3a2c');
    for (let x = 0; x < CELL_W; x += 4) px(ox + x, oy, 1, CELL_H, '#3a2c21');
  }
  // Row 6: window variety so the facade grid stops reading as a stamp.
  plasterWin(48, { curtains: true, shutters: green });
  plasterWin(49, { reflect: true, halfShut: true, shutters: red });
  {
    const [ox, oy] = cell(50);
    plasterBase(ox, oy);
    windowAt(ox, oy, { x: 12, w: 14, h: 30, y: 18 });
    windowAt(ox, oy, { x: 38, w: 14, h: 30, y: 18, curtains: true });
  }
  plasterWin(51, { x: 9, w: 20, h: 34, shutters: blue, reflect: true });
  timberWin(52, { curtains: true, frame: '#d2c4a8' });
  brickWin(53, { curtains: true, frame: '#d2c4a8' });
  plasterWin(54, { nightLit: true, curtains: true, shutters: blue });
  timberWin(55, { nightLit: true, x: 22, w: 20, shutters: red, halfShut: true, frame: '#d2c4a8' });
  const make = (canvas: HTMLCanvasElement): THREE.CanvasTexture => {
    const t = new THREE.CanvasTexture(canvas);
    t.colorSpace = THREE.SRGBColorSpace;
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestMipmapLinearFilter;
    return t;
  };
  return { map: make(c), emissiveMap: make(e) };
};

/**
 * The kit's backdrop material: the atlas, its emissive mask, vertex tint. It
 * follows the town's window-glass night ramp without a hook in the view: before
 * each draw it reads `nightReference`'s emissive intensity (the cool windows:
 * 0.06 by day, 0.4 by night), lighting the night-lit panes and dimming the
 * albedo to × 0.6 on the same ramp.
 */
export const backdropMaterial = (kit: Kit, nightReference: THREE.MeshStandardMaterial): THREE.MeshStandardMaterial =>
  kit.shared('backdrop:material', () => {
    const { map, emissiveMap } = paintAtlas();
    kit.shared('backdrop:atlas', () => map);
    kit.shared('backdrop:emission', () => emissiveMap);
    const material = new THREE.MeshStandardMaterial({ map, emissiveMap, emissive: '#ffc27a', emissiveIntensity: 0, vertexColors: true, roughness: 0.96 });
    material.onBeforeRender = () => {
      const night = Math.min(1, Math.max(0, (nightReference.emissiveIntensity - 0.06) / 0.34));
      material.emissiveIntensity = Math.max(0, (nightReference.emissiveIntensity - 0.07) * 4);
      material.color.setScalar(1 - 0.4 * night);
    };
    return material;
  });

export type V3 = readonly [number, number, number];
/** A cell's sub-rectangle [u0, v0, u1, v1] in 0..1 of the cell. */
export type Sub = readonly [number, number, number, number];
/** Faces of an atlas box: a cell per face (`all` and `side` as fallbacks); null leaves the face out. */
export type BoxCells = number | { readonly all?: number | null; readonly side?: number | null; readonly top?: number | null; readonly front?: number | null; readonly back?: number | null; readonly east?: number | null; readonly west?: number | null };

/** Accumulates atlas quads with per-vertex tint into one non-indexed geometry (`backdrop-art.js` `Kit`). */
export class AtlasKit {
  readonly pos: number[] = [];
  readonly nor: number[] = [];
  readonly uv: number[] = [];
  readonly col: number[] = [];

  static uvRect(cell: number, [u0, v0, u1, v1]: Sub = [0, 0, 1, 1]): Sub {
    const cx = (cell % 8) * CELL_W;
    const cy = Math.floor(cell / 8) * CELL_H;
    // A texel and a half inset keeps nearest sampling inside the cell.
    const U = (t: number): number => (cx + 1.5 + t * (CELL_W - 3)) / ATLAS;
    const V = (t: number): number => 1 - (cy + 1.5 + (1 - t) * (CELL_H - 3)) / ATLAS_H;
    return [U(u0), V(v0), U(u1), V(v1)];
  }

  /** a, b, c, d counter-clockwise seen from the front (bottom-left first); `shade` = [bottom, top] brightness. */
  quad(a: V3, b: V3, c: V3, d: V3, cell: number, tint: V3 = [1, 1, 1], sub?: Sub, shade: readonly [number, number] = [1, 1]): void {
    const [u0, v0, u1, v1] = AtlasKit.uvRect(cell, sub);
    const e1 = new THREE.Vector3().subVectors(new THREE.Vector3(...b), new THREE.Vector3(...a));
    const e2 = new THREE.Vector3().subVectors(new THREE.Vector3(...d), new THREE.Vector3(...a));
    const n = e1.cross(e2).normalize();
    const verts: readonly (readonly [V3, number, number, number])[] = [
      [a, u0, v0, shade[0]],
      [b, u1, v0, shade[0]],
      [c, u1, v1, shade[1]],
      [a, u0, v0, shade[0]],
      [c, u1, v1, shade[1]],
      [d, u0, v1, shade[1]],
    ];
    for (const [p, u, v, s] of verts) {
      this.pos.push(...p);
      this.nor.push(n.x, n.y, n.z);
      this.uv.push(u, v);
      this.col.push(tint[0] * s, tint[1] * s, tint[2] * s);
    }
  }

  /** An axis-aligned box; `cells` maps each face to a cell (top, front +z, back, east +x, west; no bottom). */
  box(x: number, y: number, z: number, w: number, h: number, d: number, cells: BoxCells, tint: V3 = [1, 1, 1], shade: readonly [number, number] = [0.8, 1], sub?: Sub): void {
    const x0 = x - w / 2;
    const x1 = x + w / 2;
    const y0 = y - h / 2;
    const y1 = y + h / 2;
    const z0 = z - d / 2;
    const z1 = z + d / 2;
    const c = typeof cells === 'number' ? { all: cells } : cells;
    const get = (k: 'top' | 'front' | 'back' | 'east' | 'west'): number | null | undefined => (k in c ? c[k] : (c.side ?? c.all));
    const top = get('top');
    if (top !== undefined && top !== null) this.quad([x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], top, tint, sub);
    const front = get('front');
    if (front !== undefined && front !== null) this.quad([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], front, tint, sub, shade);
    const east = get('east');
    if (east !== undefined && east !== null) this.quad([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], east, tint, sub, shade);
    const west = get('west');
    if (west !== undefined && west !== null) this.quad([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], west, tint, sub, shade);
    const back = get('back');
    if (back !== undefined && back !== null) this.quad([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], back, tint, sub, shade);
  }

  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    return g;
  }
}

/** The finished atlas geometry of a quarter as one mesh on the backdrop material (`backdrop-town.js` `finish`). */
export const atlasMesh = (parent: THREE.Object3D, kit: AtlasKit, material: THREE.Material, name: string): THREE.Mesh => {
  const m = new THREE.Mesh(kit.geometry(), material);
  m.castShadow = true;
  m.receiveShadow = true;
  m.name = name;
  parent.add(m);
  return m;
};
